<template>
	<div class="product">
		<div v-if="productIndex === 0" class="product-header">
			<md-checkbox
				v-if="genomic"
				v-model="selectedProduct"
				class="check-box md-primary"
				:class="{'md-checked': selectedProduct}"
				@change="toggleSelected"
			/>
			<h6 class="product-header-step-number">{{ step.stepNumber }}</h6>
			<h6>{{ getStepName.stemName }}</h6>
		</div>
		<div class="product-body">
			<md-checkbox
				v-if="!genomic"
				id="vuetest-checkbox"
				:class="{'md-checked': selectedProduct}"
				class="product-checkbox"
				v-model="selectedProduct"
				@change="toggleSelected"
			/>
			<div class="product-desc">
				<div class="product-desc-img">
					<base-image
						:src="{image: product.image, mobileImage: product.mobileImage}"
						:alt="product.name[getLang]"
					/>
				</div>
				<div class="product-desc-details">
					<p class="pd-title caption-2" v-html="product.name[getLang]"></p>
					<p class="pd-cat caption-small">
						Cat No./ ID: <span class="overline">{{ product.catalogNumber }}</span>
					</p>
					<p class="pd-details body-1 mobile-caption-small" v-html="product.description"></p>
				</div>
			</div>
			<div class="quantity-price-mobile">
				<div class="quantity-price-mobile-show"></div>
				<div class="quantity-price-container" v-if="!genomic">
					<div class="quantity-input">
						<dropdown-custom
							v-if="selectedQuantity < 10 && !inputInUse"
							:placeholder="'Quantity'"
							:list-options="options"
							@input="onQuantityChange"
							:value="selectedQuantityObject"
							:disabled="!selectedProduct"
						/>
						<md-field
							v-if="selectedQuantity >= 10 || inputInUse"
							:class="{'not-selected': !selectedProduct}"
						>
							<label class="caption-small" :for="product.key">Quantity</label>
							<md-input
								:name="product.key"
								id="vuetest-input"
								ref="quantityInput"
								v-model="selectedQuantity"
								@input="emitTotalPrice"
								@blur="onBlur"
								@keyup="checkMaxVal"
								type="number"
							/>
						</md-field>
					</div>
					<div class="price-container no-custom" v-if="getPrice">
						<p class="caption-2 h6" :class="{'custom-price': isDiscounted}" data-cy="product-price">
							<span v-html="getLocalizedPriceStr(totalProductPrice, currency)"></span>
						</p>
						<p class="caption-2" v-if="isDiscounted" data-cy="product-discount">
							{{ getPrice.value | getPercentDiff(customerPrice.amount) }}% off
							<span
								:class="{'strike-through': isDiscounted}"
								v-html="getLocalizedPriceStr(getConditionalPrice, currency)"
								data-cy="product-price-before-discount"
							>
							</span>
						</p>
						<p class="caption-small" data-cy="”price-per-unit”">
							Price per unit
							<span v-html="getLocalizedPriceStr(pricePerUnit, currency)"></span>
						</p>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import priceFormatter from '@/mixins/priceFormatter';
import BaseImage from '@/shared/base-img';
import DropdownCustom from '@/components/dropdown-custom/DropdownCustom';

export default {
	name: 'ProductContainer',
	components: {BaseImage, DropdownCustom},
	props: {
		productIndex: {
			type: Number,
		},
		step: {
			type: Object,
		},
		product: {
			type: Object,
		},
		country: {
			type: String,
			require: true,
			default: 'US',
		},
		step: {
			type: String | Number,
		},
		genomic: {
			type: Boolean,
			default: false,
		},
		lastStep: {
			type: Number,
		},
	},
	mixins: [priceFormatter],
	data() {
		return {
			selectedProduct: true,
			selectedQuantityObject: {
				key: 1,
				children: [],
				value: {
					enUS: '1',
				},
			},
			selectedQuantity: 1,
			inputInUse: false,
			options: [
				{key: 0, children: [], value: {enUS: '0(remove)'}, quantity: 0},
				{key: 1, children: [], value: {enUS: '1'}, quantity: 1},
				{key: 2, children: [], value: {enUS: '2'}, quantity: 2},
				{key: 3, children: [], value: {enUS: '3'}, quantity: 3},
				{key: 4, children: [], value: {enUS: '4'}, quantity: 4},
				{key: 5, children: [], value: {enUS: '5'}, quantity: 5},
				{key: 6, children: [], value: {enUS: '6'}, quantity: 6},
				{key: 7, children: [], value: {enUS: '7'}, quantity: 7},
				{key: 8, children: [], value: {enUS: '8'}, quantity: 8},
				{key: 9, children: [], value: {enUS: '9'}, quantity: 9},
				{key: 10, children: [], value: {enUS: '10+'}, quantity: 10},
			],
			customerPrice: null,
		};
	},
	computed: {
		findCustomerPrice() {
			return this.$store.getters['customerPrices/priceFor'](this.product.catalogNumber);
		},
		isDiscounted() {
			return this.customerPrice ? this.customerPrice.amount < this.getPrice.value : false;
		},
		getConditionalPrice() {
			return this.isDiscounted
				? this.getPrice.value
				: this.customerPrice
				? this.customerPrice.amount
				: this.getPrice.value;
		},
		getLang() {
			return this.$store.state.LANG || 'enUS';
		},
		getPriceStatus() {
			return this.product.priceStatuses[this.country];
		},
		getPrice() {
			if (!this.product) return;
			const price = this.product.prices.filter((price) => price.countryIsoCode === this.country)[0].price;
			return {
				Display: price, // price.value or .custom or null = no price shown
				Inquire: null, // no price shown
				Login: price, // user has to be logged into see this
				CannotOrderOnline: price, // if price and contains custom, only show value. or null
				ComingSoon: null, // no price shown
			}[this.getPriceStatus];
		},
		getPriceForTotal() {
			if (!this.getPrice) return this.getPrice;
			return this.customerPrice ? this.customerPrice.amount : this.getPrice.value;
		},
		totalProductPrice() {
			if (!this.getPrice) return;
			const price = this.customerPrice?.amount || this.getPrice.value;
			return price * Number(this.selectedQuantity);
		},
		pricePerUnit() {
			return this.customerPrice?.amount || this.getPrice.value;
		},

		currency() {
			return this.customerPrice?.currencyIsoCode || this.getPrice.currencyIsoCode;
		},
		productCatalogNumber() {
			return `${this.product.catalogNumber}-${this.step.stepNumber}`;
		},

		getStepName() {
			if (!this.$store.state.cvlWebSteps) return;
			const findStepKeyByName = (step) => step.key === this.step.name;
			const checkForName = this.$store.state.cvlWebSteps.values.find(findStepKeyByName);
			const name = checkForName ? checkForName.value.enUS : this.step.name;
			const stemName = name.length <= 160 ? name : name.slice(0, 160) + '...';
			return {stemName, stepKey: checkForName.key};
		},
	},
	watch: {
		selectedQuantity(newValue) {
			this.selectedQuantityObject = this.options.find((option) => option.quantity === newValue);
			if (newValue >= 10) {
				this.onFocus();
				if (newValue === 10) {
					this.selectedQuantity = 10;
					this.emitTotalPrice();
				}
			}
			if (newValue === '') {
				this.selectedQuantity = 0;
			}
		},
		getPriceForTotal(newValue) {
			if (newValue) {
				this.emitTotalPrice();
			}
		},
		findCustomerPrice: {
			handler(newValue) {
				this.customerPrice = newValue;
			},
			immediate: true,
		},
	},
	methods: {
		findSelectedProducts({products, selected}) {
			const selectedKeys = selected.map((product) => product.updated);
			return products.filter((product) => selectedKeys.includes(product.key));
		},
		getLocalizedPriceStr(price, currency) {
			return "<span translate='no'>" + this.formatPrice(price, currency) + '</span>';
		},
		checkMaxVal(val) {
			if (Number(val.target.value) >= 999) {
				this.selectedQuantity = 999;
			}
		},
		onBlur() {
			const selectedQ = Number(this.selectedQuantity);
			if (selectedQ <= 9 && selectedQ !== 0) {
				this.inputInUse = false;
			}
			if (selectedQ === 0) {
				this.onQuantityChange(this.options.find((option) => option.quantity === selectedQ));
				this.inputInUse = false;
			}
		},
		onFocus() {
			if (this.selectedQuantity >= 10) {
				this.$nextTick(() => this.$refs.quantityInput.$el.focus());
				this.inputInUse = true;
			}
		},
		toggleSelected() {
			if (this.genomic) {
				this.$root.$emit('stepSelected', this.step.name);
			}
			this.selectedQuantity = 1;
			this.emitTotalPrice();
		},
		onQuantityChange(option) {
			this.selectedQuantity = option.quantity;
			if (this.selectedQuantity === 0) {
				this.selectedProduct = false;
			}
			this.emitTotalPrice();
		},
		emitTotalPrice(onInput) {
			if (onInput) {
				this.selectedQuantity = Number(onInput);
			}
			const total = !this.selectedProduct ? 0 : this.totalProductPrice || 0;
			const payload = {
				total,
				key: this.productCatalogNumber,
				selected: this.selectedProduct,
				qty: this.selectedQuantity,
				catNumber: this.productCatalogNumber,
			};
			this.$emit('productTotalPrice', payload);
		},
	},
};
</script>

<style lang="scss" scoped>
.product {
	max-width: 1236px;

	&-header {
		@include flexRow;
		height: 47px;
		background-color: $color-super-light-blue;
		padding-left: 16px;
		border-bottom: 1px solid $color-light-blue;
		h6:nth-child(1) {
			margin-right: 16px;
		}

		&-step-number {
			margin-right: 10px;
		}

		@media (max-width: $mobile) {
			height: inherit;
			padding-right: 10px;
		}
	}
	&-body {
		position: relative;
		@include flexRow;
		align-items: flex-start;
		justify-content: space-between;
		padding: 24px 20px;
		padding-left: 54px;
		border-bottom: 1px solid $color-light-blue;

		@media (max-width: $mobile) {
			flex-direction: column;
			align-items: normal;
			padding: 12px 12px 24px;
		}

		.product-checkbox {
			position: absolute;
			top: 24px;
			left: 15px;
			margin: 0 !important;
		}
		.product-desc {
			@include flexRow;
			@media (min-width: $mobile + 1) {
				padding-right: 1rem;
			}
			&-img {
				margin-right: 24px;
				width: 144px;
				min-width: 144px;
				@media (max-width: $mobile) {
					min-width: 80px;
					width: 80px;
				}
			}
			&-details {
				max-width: 670px;
				width: 100%;
			}
			.pd-title {
				color: $color-blue;
				margin-bottom: 4px;
			}
			.pd-cat {
				margin-bottom: 8px;
			}
			.pd-details {
				margin-bottom: 0;
			}
		}

		.quantity-price-mobile {
			display: flex;
			flex-direction: row;

			&-show {
				margin-right: 24px;
				width: 80px;
				display: none;

				@media (max-width: $mobile) {
					display: block;
				}
			}
		}

		.quantity-price-container {
			flex: 1;
			@include flexRow;
			align-items: flex-start;
			justify-content: space-between;
			flex-direction: column;

			@media (max-width: $mobile) {
				margin-top: 20px;
			}

			.quantity-input {
				width: 104px;
				height: 40px;
				position: relative;
				margin-left: 34px;
				margin-bottom: 20px;

				.dropdown-toggle.btn {
					height: 40px;
				}

				@media (max-width: $mobile) {
					margin-left: 0px;
				}

				.md-field {
					width: 104px;
					height: 40px;
					min-height: 40px;
					border-radius: 10px;
					padding: 6px 16px;
					border: 1px solid $color-grey;
					label {
						top: -10px;
						left: 12px;
						background-color: $white;
						padding: 0 4px;
						color: $color-grey;
						@include font-styles('Futura W01 Book', 400);
						@include textStyles(13, 20, 0.4);
					}
					.md-menu,
					.md-menu .md-icon-image svg {
						fill: $color-text-grey;
					}
					&.md-focused {
						border: 2px solid $color-blue;
						label {
							color: $color-blue;
						}
						&.not-selected {
							border: 1px solid $color-grey;
							label {
								color: $color-grey;
							}
							.md-menu {
								color: $color-text-grey;
							}
						}
					}
					&.md-disabled {
						cursor: not-allowed;
					}
				}
				.md-menu.md-select {
					overflow: hidden;
					.md-input {
						height: 100%;
						width: 100%;
					}
				}
				.md-field input.md-input {
					height: 100%;
					width: 100%;
					&[type='number']::-webkit-inner-spin-button,
					&[type='number']::-webkit-outer-spin-button {
						-webkit-appearance: none;
						margin: 0;
					}
				}
			}
			.price-container {
				min-width: 124px;
				width: auto;
				@include flexColumn;
				text-align: end;

				@media (max-width: $mobile) {
					text-align: start;
				}

				p {
					margin: 0 0 3px 0;
				}
				p:nth-child(1).custom-price {
					font-size: 20px;
					color: $color-red;
				}
				.strike-through {
					text-decoration: line-through;
				}
			}
		}
	}
}
</style>
